import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Store, User, LogOut, LayoutDashboard, ShoppingBag, Shield, Upload, MessageSquare } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

export const Navbar = () => {
  const { user, logout, isAuthenticated, isArtist, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1 group" data-testid="nav-logo">
            <img
              src="/ziki-logo-green-128.png"
              alt="Z-Store"
              className="h-8 w-8 object-contain shrink-0"
            />
            <span className="font-bold text-2xl leading-none tracking-tight text-emerald-500 group-hover:text-emerald-400 transition-colors">
              -Store
            </span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/marketplace"
              className="text-sm text-slate-400 hover:text-white transition-colors"
              data-testid="nav-marketplace"
            >
              Marketplace
            </Link>
            <Link
              to="/artists"
              className="text-sm text-slate-400 hover:text-white transition-colors"
              data-testid="nav-artists"
            >
              Artists
            </Link>
            {isAuthenticated && (
              <Link
                to="/purchases"
                className="text-sm text-slate-400 hover:text-white transition-colors"
                data-testid="nav-purchases"
              >
                My Licenses
              </Link>
            )}
            {isAuthenticated && (
              <Link
                to="/inbox"
                className="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1.5"
                data-testid="nav-inbox"
              >
                <MessageSquare className="w-4 h-4" />
                Inbox
              </Link>
            )}
            {isArtist && (
              <Link
                to="/dashboard?upload=1"
                className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors font-medium"
                data-testid="nav-upload-track"
              >
                <Upload className="w-4 h-4" />
                Upload Track
              </Link>
            )}
          </div>

          {/* Auth Buttons */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-slate-400 hover:text-white"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </div>
                    <span className="hidden md:inline text-sm">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-slate-900 border-white/10" align="end">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-slate-400">{user?.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-400 rounded-full capitalize">
                      {user?.role}
                    </span>
                  </div>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {isArtist && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/dashboard')}
                      className="cursor-pointer text-slate-300 hover:text-white focus:text-white focus:bg-white/5"
                      data-testid="dropdown-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Artist Dashboard
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/admin')}
                      className="cursor-pointer text-slate-300 hover:text-white focus:text-white focus:bg-white/5"
                      data-testid="dropdown-admin"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={() => navigate('/purchases')}
                    className="cursor-pointer text-slate-300 hover:text-white focus:text-white focus:bg-white/5"
                    data-testid="dropdown-purchases"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    My Licenses
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-red-500/10"
                    data-testid="dropdown-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-slate-400 hover:text-white"
                  data-testid="nav-login"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold rounded-lg"
                  data-testid="nav-register"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
